import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, RotateCcw, Lock } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Per-workshop module overrides.
 *
 * A workshop keeps the package it joined with, so this is where you give one
 * of them a module their package does not include, or take one away. Removing
 * a module hides it but leaves their records readable.
 */
const TenantModulesDialog = ({ tenant, modules, coreModules, onClose, onSaved }) => {
  const { authHeader } = useAuth();
  const [addons, setAddons] = useState(tenant.module_addons || []);
  const [removals, setRemovals] = useState(tenant.module_removals || []);
  const [saving, setSaving] = useState(false);

  const packaged = useMemo(
    () => new Set(tenant.features_snapshot || tenant.access?.features || []),
    [tenant],
  );

  const groups = useMemo(() => {
    const out = [];
    (modules || []).forEach((m) => {
      const g = out.find((x) => x.name === m.group);
      if (g) g.items.push(m); else out.push({ name: m.group, items: [m] });
    });
    return out;
  }, [modules]);

  const stateOf = (key) => {
    if (removals.includes(key)) return 'removed';
    if (addons.includes(key)) return 'added';
    return packaged.has(key) ? 'included' : 'off';
  };

  const cycle = (key) => {
    if (coreModules.includes(key)) return;
    const s = stateOf(key);
    if (s === 'included') { setRemovals([...removals, key]); return; }
    if (s === 'removed') { setRemovals(removals.filter((k) => k !== key)); return; }
    if (s === 'added') { setAddons(addons.filter((k) => k !== key)); return; }
    setAddons([...addons, key]);
  };

  const save = async (resync = false) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/platform/tenants/${tenant.id}/modules`,
        resync ? { resync: true, addons: [], removals: [] } : { addons, removals },
        { headers: authHeader() },
      );
      toast.success(resync
        ? `${tenant.workshop_name} moved onto the current ${tenant.plan} package`
        : `Modules updated for ${tenant.workshop_name}`,
      { description: `${data.features.length} modules active${data.view_only_features.length ? `, ${data.view_only_features.length} view-only` : ''}.` });
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update modules');
    } finally {
      setSaving(false);
    }
  };

  const TONE = {
    included: 'bg-teal-500 border-teal-500 text-white',
    added: 'bg-sky-500 border-sky-500 text-white',
    removed: 'bg-white border-red-300 text-red-600 line-through',
    off: 'bg-white border-slate-300 text-slate-500',
  };
  const LABEL = { included: 'In package', added: 'Added on', removed: 'Removed', off: 'Not included' };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
           data-testid="tenant-modules-dialog">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{tenant.workshop_name} · modules</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              On the <b>{tenant.plan}</b> package as it was when they joined. Tap a module to
              add it on or take it away.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="tenant-modules-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {groups.map((g) => (
            <div key={g.name}>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-2">{g.name}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {g.items.map((m) => {
                  const s = stateOf(m.key);
                  const isCore = coreModules.includes(m.key);
                  return (
                    <button
                      key={m.key}
                      type="button"
                      disabled={isCore}
                      onClick={() => cycle(m.key)}
                      className={`text-left border rounded-xl px-3 py-2.5 transition-colors ${TONE[s]} ${
                        isCore ? 'opacity-70 cursor-not-allowed' : 'hover:brightness-95'}`}
                      data-testid={`tenant-module-${m.key}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{m.label}</span>
                        {isCore ? <Lock size={12} /> : (
                          <span className={`text-[10px] uppercase tracking-wider ${
                            s === 'included' || s === 'added' ? 'text-white/85' : 'text-slate-400'}`}>
                            {LABEL[s]}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => save(true)}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
            data-testid="tenant-modules-resync"
          >
            <RotateCcw size={14} /> Move onto the current package
          </button>
          <button
            onClick={() => save(false)}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-5 py-2.5 rounded-xl"
            data-testid="tenant-modules-save"
          >
            {saving && <Loader2 className="animate-spin" size={15} />}
            Save modules
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantModulesDialog;
